/// <reference types="vite/client" />
import { createClient, SupabaseClient } from "@supabase/supabase-js";
import { Product, Inquiry, SiteSettings, Post } from "../types";

let cachedClient: SupabaseClient | null = null;
let cachedUrl = "";
let cachedKey = "";

// Retrieves or instantiates Supabase Client dynamically based on current user settings
export function getSupabaseClient(settings: SiteSettings): SupabaseClient | null {
  const envUrl = import.meta.env.VITE_SUPABASE_URL;
  const envKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  
  const url = (envUrl || settings?.supabaseUrl)?.trim();
  const key = (envKey || settings?.supabaseAnonKey)?.trim();

  if (!url || !key) {
    return null;
  }

  // Reuse cached client if credentials have not changed to optimize connection pools
  if (cachedClient && cachedUrl === url && cachedKey === key) {
    return cachedClient;
  }

  try {
    cachedUrl = url;
    cachedKey = key;
    cachedClient = createClient(url, key); // مدیریت Session به حالت خودکار و پایدار تغییر کرد
    return cachedClient;
  } catch (error) {
    console.error("Failed to initialize Supabase connection client:", error);
    return null;
  }
}

/* === STORAGE UPLOAD IMPLEMENTATION === */

export async function dbUploadProductImage(settings: SiteSettings, file: File): Promise<string | null> {
  const client = getSupabaseClient(settings);
  if (!client) {
    return null;
  }

  try {
    const fileExt = file.name.split(".").pop();
    const uniqueId = Math.random().toString(36).substring(2, 9);
    const fileName = `${Date.now()}-${uniqueId}.${fileExt}`;
    const filePath = `products/${fileName}`;

    // Upload the file to the 'product-images' bucket
    const { error } = await client.storage
      .from("product-images")
      .upload(filePath, file, {
        cacheControl: "3600",
        upsert: false,
      });

    if (error) {
      console.warn("Storage upload failed on Supabase bucket:", error.message);
      throw error;
    }

    // Get public URL of the uploaded image file
    const { data: publicUrlData } = client.storage
      .from("product-images")
      .getPublicUrl(filePath);

    return publicUrlData.publicUrl;
  } catch (err) {
    console.error("Error inside dbUploadProductImage:", err);
    return null;
  }
}

/**
 * هوشمندسازی لود تصاویر جهت بهبود ۱۰۰٪ نمره PageSpeed
 * این تابع به صورت هوشمند تصاویر را بهینه می‌کند.
 */
export function getOptimizedImageUrl(url: string | undefined, width: number = 800, quality: number = 75): string {
  if (!url) return "";
  
  // 1. بهینه‌سازی تصاویر Unsplash (رایگان با پارامتر)
  if (url.includes("unsplash.com")) {
    const baseUrl = url.split("?")[0];
    return `${baseUrl}?q=${quality}&w=${width}&fm=webp&auto=format&fit=crop`;
  }
  
  // 2. بهینه‌سازی تصاویر سایر منابع (مخصوصاً Supabase Free Plan)
  // از سرویس رایگان wsrv.nl استفاده می‌کنیم که تصاویر را به WebP تبدیل و ریسایز می‌کند.
  if (url.includes(".supabase.co/storage/v1/object/public/") || url.startsWith("http")) {
     // جلوگیری از پردازش مجدد اگر لینک قبلاً از پروکسی عبور کرده باشد
     if (url.includes("wsrv.nl")) return url;
     
     const cleanUrl = url.trim();
     return `https://wsrv.nl/?url=${encodeURIComponent(cleanUrl)}&w=${width}&q=${quality}&output=webp`;
  }
  
  return url;
}

export async function dbDeleteStorageFile(settings: SiteSettings, publicUrl: string, filePath?: string, bucketName: string = "product-images"): Promise<boolean> {
  const client = getSupabaseClient(settings);
  if (!client || !publicUrl) return false;

  try {
    let pathToDelete = filePath;
    
    if (!pathToDelete) {
      // Extract file path from URL if not provided
      const parts = publicUrl.split(`/${bucketName}/`);
      if (parts.length < 2) return false;
      pathToDelete = parts[1];
    }

    const { error } = await client.storage.from(bucketName).remove([pathToDelete]);
    if (error) throw error;
    return true;
  } catch (err) {
    console.error("Failed to delete file from storage:", err);
    return false;
  }
}

export async function dbListStorageFiles(settings: SiteSettings, bucketNames: string[] = ["product-images", "platform-assets"]): Promise<any[]> {
  const client = getSupabaseClient(settings);
  if (!client) return [];

  try {
    let finalFiles: any[] = [];
    
    for (const bName of bucketNames) {
       // Paths to check in each bucket
       const pathsToCheck = ["", "products"];
       
       for (const path of pathsToCheck) {
         const { data: files, error } = await client.storage.from(bName).list(path, {
           limit: 200,
           sortBy: { column: "created_at", order: "desc" }
         });

         if (!error && files) {
           files.forEach(f => {
             if (f.id) { // It's a file, not a virtual folder
               const fullPath = path ? `${path}/${f.name}` : f.name;
               const { data: { publicUrl } } = client.storage.from(bName).getPublicUrl(fullPath);
               finalFiles.push({ ...f, publicUrl, path: fullPath, bucket: bName });
             }
           });
         }
       }
    }

    // Deduplicate by publicUrl
    const uniqueFiles = Array.from(new Map(finalFiles.map(f => [f.publicUrl, f])).values());
    return uniqueFiles.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  } catch (err) {
    console.error("Failed to list storage files:", err);
    return [];
  }
}

async function listFolderFiles(client: any, bucketName: string, folderName: string) {
    const { data: files } = await client.storage.from(bucketName).list(folderName);
    if (!files) return [];
    return files.filter((f: any) => f.id).map((f: any) => {
        const { data: { publicUrl } } = client.storage.from(bucketName).getPublicUrl(`${folderName}/${f.name}`);
        return { ...f, publicUrl, path: `${folderName}/${f.name}`, bucket: bucketName };
    });
}

/* === SETTINGS CLOUD SYNC IMPLEMENTATIONS === */

export async function dbFetchSiteSettings(settings: SiteSettings, tenantId: string = "default"): Promise<Partial<SiteSettings> | null> {
  const client = getSupabaseClient(settings);
  if (!client) return null;
  try {
    const { data, error } = await client.from("site_settings").select("config").eq("id", tenantId).maybeSingle();
    if (error) throw error;
    return data ? data.config : null;
  } catch (err) {
    console.warn("Failed to fetch settings from cloud (Table might not exist yet):", err);
    return null;
  }
}

export async function dbUpsertSiteSettings(settings: SiteSettings, config: SiteSettings, tenantId: string = "default"): Promise<boolean> {
  const client = getSupabaseClient(settings);
  if (!client) return false;
  try {
    // کلیدهای ووکامرس به دلایل امنیتی در دیتابیس عمومی ابری ذخیره نمی‌شوند
    const { supabaseUrl, supabaseAnonKey, wpUrl, wooConsumerKey, wooConsumerSecret, ...safeConfig } = config;
    const { error } = await client.from("site_settings").upsert({ id: tenantId, config: safeConfig }, { onConflict: "id" });
    if (error) throw error;
    return true;
  } catch (err) {
    console.error("Failed to upsert settings to Cloud:", err);
    return false;
  }
}

/* === PRODUCTS CRUD IMPLEMENTATIONS === */

const normalizeCategory = (cat: any): string[] => {
  if (Array.isArray(cat)) return cat;
  try {
    const parsed = JSON.parse(cat);
    if (Array.isArray(parsed)) return parsed;
  } catch {}
  if (typeof cat === "string") {
    return cat.includes(",") ? cat.split(",").map(c => c.trim()) : [cat];
  }
  return ["بدون دسته‌بندی"];
};

export async function dbFetchProducts(settings: SiteSettings, fallbackProducts: Product[], tenantId: string = "default"): Promise<Product[]> {
  const client = getSupabaseClient(settings);
  if (!client) {
    const local = localStorage.getItem(`sarvarpayman_products_${tenantId}`);
    if (local) {
      try {
        const parsed = JSON.parse(local);
        return parsed.map((p: any) => ({ ...p, category: normalizeCategory(p.category) }));
      } catch {
        return fallbackProducts;
      }
    }
    return fallbackProducts;
  }

  try {
    const { data, error } = await client
      .from("products")
      .select("*")
      .eq("tenant_id", tenantId)
      .order("created_at", { ascending: false });

    if (error) {
      console.warn("Supabase fetch products query returned error. Falling back to local state:", error.message);
      throw error;
    }

    if (data && data.length > 0) {
      return data.map((item: Record<string, any>) => ({
        id: item.id,
        name: item.name,
        sku: item.sku,
        category: normalizeCategory(item.category),
        price: Number(item.price),
        image: item.image,
        description: item.description,
        specs: typeof item.specs === "string" ? JSON.parse(item.specs) : item.specs || {},
        stock: item.stock ?? true,
        isFeatured: item.isFeatured ?? false,
      }));
    }
    return fallbackProducts;
  } catch (err) {
    console.error("Supabase products syncing failed. Using local products:", err);
    const local = localStorage.getItem(`sarvarpayman_products_${tenantId}`);
    const parsed = local ? JSON.parse(local) : fallbackProducts;
    return parsed.map((p: any) => ({ ...p, category: normalizeCategory(p.category) }));
  }
}

export async function dbUpsertProduct(settings: SiteSettings, product: Product, tenantId: string = "default"): Promise<boolean> {
  const client = getSupabaseClient(settings);
  if (!client) {
    return false; // Handled client-side via state & LocalStorage
  }

  try {
    const payload = {
      id: product.id,
      name: product.name,
      sku: product.sku,
      category: JSON.stringify(product.category),
      price: product.price,
      image: product.image,
      description: product.description,
      specs: product.specs,
      stock: product.stock,
      isFeatured: product.isFeatured || false,
      tenant_id: tenantId,
    };

    const { error } = await client
      .from("products")
      .upsert(payload, { onConflict: "id" });

    if (error) throw error;
    return true;
  } catch (err) {
    console.error("Failed to upsert product in Supabase Cloud:", err);
    return false;
  }
}

export async function dbDeleteProduct(settings: SiteSettings, id: string): Promise<boolean> {
  const client = getSupabaseClient(settings);
  if (!client) return false;

  try {
    const { error } = await client.from("products").delete().eq("id", id);
    if (error) throw error;
    return true;
  } catch (err) {
    console.error("Failed to delete product in Supabase Cloud:", err);
    return false;
  }
}

export async function dbBulkDeleteProducts(settings: SiteSettings, ids: string[]): Promise<boolean> {
  const client = getSupabaseClient(settings);
  if (!client) return false;

  try {
    const { error } = await client.from("products").delete().in("id", ids);
    if (error) throw error;
    return true;
  } catch (err) {
    console.error("Failed to bulk delete products in Supabase Cloud:", err);
    return false;
  }
}

export async function dbBulkUpsertProducts(settings: SiteSettings, products: Product[], tenantId: string = "default"): Promise<boolean> {
  const client = getSupabaseClient(settings);
  if (!client) return false;

  try {
    const payloads = products.map(product => ({
      id: product.id,
      name: product.name,
      sku: product.sku,
      category: JSON.stringify(product.category),
      price: product.price,
      image: product.image,
      description: product.description,
      specs: product.specs,
      stock: product.stock,
      isFeatured: product.isFeatured || false,
      tenant_id: tenantId,
    }));

    const { error } = await client.from("products").upsert(payloads, { onConflict: "id" });
    if (error) throw error;
    return true;
  } catch (err) {
    console.error("Failed to bulk upsert products in Supabase Cloud:", err);
    return false;
  }
}

/* === INQUIRIES CRUD IMPLEMENTATIONS === */

export async function dbFetchInquiries(settings: SiteSettings, tenantId: string = "default"): Promise<Inquiry[]> {
  const client = getSupabaseClient(settings);
  if (!client) {
    const local = localStorage.getItem(`sarvarpayman_inquiries_${tenantId}`);
    return local ? JSON.parse(local) : [];
  }

  try {
    const { data, error } = await client
      .from("inquiries")
      .select("*")
      .eq("tenant_id", tenantId)
      .order("createdAt", { ascending: false });

    if (error) throw error;

    if (data) {
      return data.map((item: Record<string, any>) => ({
        id: item.id,
        customerName: item.customerName,
        phone: item.phone,
        email: item.email || "",
        company: item.company || "",
        message: item.message || "",
        createdAt: item.createdAt,
        items: typeof item.items === "string" ? JSON.parse(item.items) : item.items || [],
        status: item.status || "new",
        totalPrice: Number(item.totalPrice) || 0,
      }));
    }
    return [];
  } catch (err) {
    console.error("Failed to fetch inquiries from Supabase Cloud:", err);
    const local = localStorage.getItem(`sarvarpayman_inquiries_${tenantId}`);
    return local ? JSON.parse(local) : [];
  }
}

export async function dbInsertInquiry(settings: SiteSettings, inquiry: Inquiry, tenantId: string = "default"): Promise<boolean> {
  const client = getSupabaseClient(settings);
  if (!client) return false;

  try {
    const payload = {
      id: inquiry.id,
      customerName: inquiry.customerName,
      phone: inquiry.phone,
      email: inquiry.email,
      company: inquiry.company,
      message: inquiry.message,
      items: inquiry.items,
      status: inquiry.status,
      totalPrice: inquiry.totalPrice,
      createdAt: inquiry.createdAt,
      tenant_id: tenantId,
    };

    const { error } = await client.from("inquiries").insert(payload);
    if (error) throw error;
    return true;
  } catch (err) {
    console.error("Failed to submit inquiry to Supabase Cloud Instance:", err);
    return false;
  }
}

export async function dbDeleteInquiry(settings: SiteSettings, id: string): Promise<boolean> {
  const client = getSupabaseClient(settings);
  if (!client) return false;

  try {
    const { error } = await client.from("inquiries").delete().eq("id", id);
    if (error) throw error;
    return true;
  } catch (err) {
    console.error("Failed to delete inquiry from Supabase Cloud Row:", err);
    return false;
  }
}

export async function dbUpdateInquiryStatus(settings: SiteSettings, id: string, status: 'new' | 'viewed' | 'completed'): Promise<boolean> {
  const client = getSupabaseClient(settings);
  if (!client) return false;

  try {
    const { error } = await client
      .from("inquiries")
      .update({ status })
      .eq("id", id);

    if (error) throw error;
    return true;
  } catch (err) {
    console.error("Failed to toggle status in Supabase Cloud Row:", err);
    return false;
  }
}

/* === BLOG / POSTS IMPLEMENTATIONS === */

export async function dbFetchPosts(settings: SiteSettings, tenantId: string = "default"): Promise<Post[]> {
  const client = getSupabaseClient(settings);
  if (!client) return [];

  try {
    const { data, error } = await client
      .from("posts")
      .select("*")
      .eq("tenant_id", tenantId)
      .order("createdAt", { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (err) {
    console.error("Failed to fetch posts:", err);
    return [];
  }
}

export async function dbUpsertPost(settings: SiteSettings, post: Post): Promise<boolean> {
  const client = getSupabaseClient(settings);
  if (!client) return false;

  try {
    const { error } = await client
      .from("posts")
      .upsert(post, { onConflict: "id" });

    if (error) throw error;
    return true;
  } catch (err) {
    console.error("Failed to upsert post:", err);
    return false;
  }
}

export async function dbDeletePost(settings: SiteSettings, id: string): Promise<boolean> {
  const client = getSupabaseClient(settings);
  if (!client) return false;

  try {
    const { error } = await client
      .from("posts")
      .delete()
      .eq("id", id);

    if (error) throw error;
    return true;
  } catch (err) {
    console.error("Failed to delete post:", err);
    return false;
  }
}
